import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ChatPaneHeader from "./ChatPaneHeader";
import ChatHistoryList from "./ChatHistoryList";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import { startNewChat } from "../../store/actions/chatbot";
import "./Chatbot.scss";

function ChatPane() {
  const dispatch = useDispatch();
  const chatPaneOpen = useSelector((s) => s.chatbot.chatPaneOpen);
  const historyViewActive = useSelector((s) => s.chatbot.historyViewActive);
  const currentSessionId = useSelector((s) => s.chatbot.currentSessionId);
  const initialised = useRef(false);
  const paneRef = useRef(null);

  // On first open: start a fresh chat and load quick questions
  useEffect(() => {
    if (chatPaneOpen && !initialised.current) {
      initialised.current = true;
      dispatch(startNewChat());
    }
  }, [chatPaneOpen]);

  // Lock body scroll while the pane is open so the page scrollbar never appears.
  // Add padding-right equal to the scrollbar width to prevent layout shift.
  useEffect(() => {
    if (chatPaneOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [chatPaneOpen]);

  // Prevent wheel events from ever reaching the page body when cursor is over the pane.
  // Without this, fast scrolls slip past overscroll-behavior: contain and briefly give
  // the body a scrollbar, which shifts the entire layout right by ~15px then back.
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;

    const handleWheel = (e) => {
      const list = pane.querySelector(".chat-message-list, .chat-history-list");
      if (!list) {
        e.preventDefault();
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = list;
      const atTop    = scrollTop <= 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

      if (atTop && e.deltaY < 0) {
        // Trying to scroll up past the top — block page scroll
        e.preventDefault();
      } else if (atBottom && e.deltaY > 0) {
        // Trying to scroll down past the bottom — block page scroll
        e.preventDefault();
      }
      // Otherwise: list can scroll, don't prevent default — native scroll handles it
    };

    pane.addEventListener("wheel", handleWheel, { passive: false });
    return () => pane.removeEventListener("wheel", handleWheel);
  }, []);

  return (
    <div ref={paneRef} className={`chat-pane${chatPaneOpen ? " chat-pane--open" : ""}`}>
      <ChatPaneHeader />

      {historyViewActive ? (
        <ChatHistoryList />
      ) : (
        <>
          <MessageList />
          <MessageInput />
        </>
      )}
    </div>
  );
}

export default ChatPane;
